import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@arcadeai/design-system/components/ui/atoms/card";
import { Zap } from "lucide-react";
import type React from "react";
import { toFriendlyMetricName } from "@/app/billing/utils";
import type { TransformedUsage } from "../types";

const MAX_PERCENTAGE = 100;
const PERCENTAGE_MULTIPLIER = 100;

type Allocation = {
  name: string;
  quantity: number | null;
  currency: string | undefined;
};

type UsageAllocationsCardProps = {
  allocations: Allocation[];
  transformedUsage: TransformedUsage[];
  metricKeys: string[];
  planName: string;
};

export const UsageAllocationsCard: React.FC<UsageAllocationsCardProps> = ({
  allocations,
  transformedUsage,
  metricKeys,
  planName,
}) => (
  <Card className="mt-8">
    <CardHeader className="pb-3">
      <CardTitle className="flex items-center gap-2 text-lg">
        <Zap className="h-4 w-4 text-brand-text" />
        Usage Allocations
      </CardTitle>
      <CardDescription>
        Monthly quotas included in your {planName} plan
      </CardDescription>
    </CardHeader>
    <CardContent>
      <div className="mt-2 grid grid-cols-1 gap-10 md:grid-cols-2">
        {allocations.map((alloc) => {
          let used = 0;
          if (transformedUsage.length && metricKeys.length) {
            const matchKey = metricKeys.find((k) =>
              alloc.currency ? k.includes(alloc.currency) : false
            );
            if (matchKey) {
              used =
                // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
                (transformedUsage.at(-1) as Record<string, number>)[matchKey] ??
                0;
            }
          }
          const percentage = alloc.quantity
            ? Math.min(
                MAX_PERCENTAGE,
                (used / alloc.quantity) * PERCENTAGE_MULTIPLIER
              )
            : 0;
          return (
            <div className="space-y-2" key={alloc.name}>
              <div className="flex items-center justify-between">
                <h4 className="font-medium text-sm">
                  {toFriendlyMetricName(alloc.name)}
                </h4>
                <div className="flex items-center gap-2">
                  <span className="rounded-full bg-muted px-2 py-0.5 font-medium text-foreground text-xs">
                    {percentage.toFixed(0)}%
                  </span>
                  <span className="font-medium text-sm">
                    {alloc.quantity?.toLocaleString()}
                  </span>
                </div>
              </div>
              <div className="space-y-1.5">
                <div className="h-2 w-full rounded-full bg-muted">
                  <div
                    className="h-2 rounded-full bg-primary"
                    style={{ width: `${percentage}%` }}
                  />
                </div>
                <div className="flex justify-between text-muted-foreground text-xs">
                  <span>{used.toLocaleString()} used</span>
                  <span>{alloc.quantity?.toLocaleString()} available</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </CardContent>
  </Card>
);
